import { Grid, IconButton, Tab, Tabs, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TabContext, TabPanel } from '@mui/lab';
import Typography from '@mui/material/Typography';
import styled from 'styled-components';
import { Edit, Email, FileDownload, Redeem } from '@mui/icons-material';
import ConfirmationNumber from '@mui/icons-material/ConfirmationNumber';
import EventParticipantsTable from './EventParticipantsTable';
import {
  AccountData,
  EnrichedEventData,
  MessageRecipientType,
  OrderData,
  WaitingListItemData,
  WaitingListService,
} from '../../../../api';
import { CellProps, useTable } from '../../../../table/useTable';
import { handleVouchersDownload } from './SaveToCsv';
import { TABLE_PHONE_ID } from '../../../../table/TableComponent';
import TimeUtils from '../../../../common/TimeUtils';
import { getDateFormat, getTimeFormat } from '../../../../common/getDateAndTimeFormat';
import { useAuth } from '../../../../session/InternalAuthProvider';
import { useCalendar } from '../../../CalendarContext/CalendarContext';
import { WaitingListUpdateDialog } from '../../../../waitingList/WaitingListUpdateDialog';
import EditOrderDialog from '../../../dialogs/EditOrderDialog/EditOrderDialog';
import OrderBoxDialog from '../../OrderBoxDialog';
import SendEmailDialog from '../../../dialogs/SendEmailDialog/SendEmailDialog';
import { UseAuthPermissions } from '../../../../session/UseAuthPermissions';

export const waitingListFetcher =
  (eventId: string) =>
  async ({ search, page, perPage }: { search?: string; page: number; perPage: number }) =>
    WaitingListService.getWaitingList(eventId, page, perPage, search);

type TablesTabsProps = {
  event: EnrichedEventData;
  orderPage: any;
  setOrder: (order: OrderData) => void;
  setEditOrderIsOpen: (isOpen: boolean) => void;
  setCheckInDialogIsOpen: (isOpen: boolean) => void;
  isInEventMode: boolean;
  activeTab: ParticipantsTableTabValue;
  setActiveTab: (tab: ParticipantsTableTabValue) => void;
  waitingListUpdateDialogIsOpen: boolean;
  setWaitingListUpdateDialogIsOpen: (isOpen: boolean) => void;
};
const StyledWaitingListNotEmpty = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
`;

export enum ParticipantsTableTabValue {
  ORDERS = '0',
  WAITING_LIST = '1',
}

export const TablesTabs = ({
  event,
  orderPage,
  setOrder,
  setEditOrderIsOpen,
  setCheckInDialogIsOpen,
  isInEventMode,
  activeTab,
  setActiveTab,
  waitingListUpdateDialogIsOpen,
  setWaitingListUpdateDialogIsOpen,
}: TablesTabsProps) => {
  const { users, editOrderDrawerState, closeEditOrderDrawer, openEditOrderDrawer } = useCalendar();
  const { readOnly } = UseAuthPermissions();

  const [orderOpen, setOrderOpen] = useState(false);
  const [orderId, setOrderId] = useState<string | undefined>(undefined);
  const [selectedWaitingListItem, setSelectedWaitingListItem] = useState<WaitingListItemData | undefined>();
  const [showSendEmailDialog, setShowSendEmailDialog] = useState(false);
  const theme = useTheme();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const { shortDateFormat } = getDateFormat(account);
  const timeFormat = getTimeFormat(account);

  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const height = window.innerHeight;
  const handleChange = (_: any, newValue: ParticipantsTableTabValue) => {
    setActiveTab(newValue);
  };
  const tableDetails: CellProps<WaitingListItemData>[] = [
    {
      id: 'name',
      width: 140,
      alignment: 'inherit',
      label: t('waitingList.name'),
      render: (waitingListItem: WaitingListItemData) => `${waitingListItem.firstname} ${waitingListItem.lastname}`,
    },
    {
      id: 'persons',
      width: 30,
      alignment: 'center',
      label: t('waitingList.persons'),
      render: (waitingListItem: WaitingListItemData) => waitingListItem.persons,
    },
    {
      id: TABLE_PHONE_ID,
      alignment: 'center',
      label: t('waitingList.phone'),
      width: 140,
    },
    {
      id: 'email',
      width: 140,
      alignment: 'center',
      label: t('waitingList.email'),
      render: (waitingListItem: WaitingListItemData) => (
        <Typography style={{ textTransform: 'none' }}>{waitingListItem.email}</Typography>
      ),
    },
    {
      id: 'createdat',
      width: 100,
      alignment: 'center',
      label: t('waitinglist.createdat'),
      render: (waitingListItem: WaitingListItemData) => (
        <Typography>
          {TimeUtils.getLocalTime(waitingListItem.created_at, account.timezone).format(
            `${shortDateFormat} ${timeFormat}`,
          )}
        </Typography>
      ),
    },
    {
      id: 'status',
      alignment: 'center',
      label: t('waitinglist.statuss'),
      render: (waitingListItem: WaitingListItemData) => (
        <Typography>{t(`waitinglist.status.${waitingListItem.status?.toLowerCase()}`)}</Typography>
      ),
    },
    {
      id: 'comment',
      alignment: 'center',
      label: t('waitinglist.comment'),
      render: (waitingListItem: WaitingListItemData) => (
        <Typography
          sx={{
            maxWidth: '250px',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
          }}>
          {waitingListItem.comment || ''}
        </Typography>
      ),
    },
    {
      id: 'lastEditedBy',
      alignment: 'center',
      label: t('waitinglist.lastEditedBy'),
      render: (waitingListItem: WaitingListItemData) => {
        const user = users.find((user) => user.user_id === waitingListItem.last_updated_by_user_id);
        return <Typography noWrap>{user ? `${user.first_name} ${user.last_name}` : ''}</Typography>;
      },
    },
    {
      id: 'orderlink',
      alignment: 'left',
      width: 30,
      noPadding: true,
      label: t('vouchers.orderlink'),
      render: (waitingListItem: WaitingListItemData) =>
        waitingListItem.order_id ? (
          <Tooltip title={t('info')} arrow>
            <span>
              <IconButton
                onClick={() => {
                  setOrderOpen(true);
                  setOrderId(waitingListItem.order_id);
                }}>
                <ConfirmationNumber />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          ''
        ),
    },
    {
      id: 'redeem',
      alignment: 'left',
      width: 30,
      noPadding: true,
      render: (waitingListItem: WaitingListItemData) => (
        <Tooltip
          title={waitingListItem.order_id ? t('waitinglist.redeemdisabled') : t('waitinglist.convertToOrder')}
          arrow>
          <span>
            <IconButton
              onClick={() =>
                openEditOrderDrawer({
                  orderPageId: waitingListItem.order_page_id,
                  waitingListItem,
                })
              }
              disabled={!!waitingListItem.order_id}>
              <Redeem />
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
    {
      id: 'edit',
      alignment: 'left',
      width: 30,
      render: (waitingListItem: WaitingListItemData) => (
        <Tooltip title={t('edit')}>
          <span>
            <IconButton
              className="action-icon"
              onClick={() => {
                setSelectedWaitingListItem(waitingListItem);
                setWaitingListUpdateDialogIsOpen(true);
              }}>
              <Edit />
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
  ];
  const {
    TableComponent: WaitingListTableComponent,
    data,
    isLoadingData,
    mutate,
  } = useTable<WaitingListItemData>({
    tableDetails,
    hidePaging: true,
    defaultRowsPerPage: 1000,
    opId: 'getWaitingList',
    fetcher: waitingListFetcher(event.id),
    actionsComponent: (
      <>
        <Tooltip title={readOnly ? t('noPermissions') : t('waitinglist.sendMessage')} arrow>
          <span>
            <IconButton onClick={() => setShowSendEmailDialog(true)}>
              <Email />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={readOnly ? t('noPermissions') : t('waitinglist.downloadWaitlist')} arrow>
          <span>
            <IconButton onClick={() => handleVouchersDownload(event, data)}>
              <FileDownload />
            </IconButton>
          </span>
        </Tooltip>
        <SendEmailDialog
          isOpen={showSendEmailDialog}
          setIsOpen={setShowSendEmailDialog}
          eventId={event.id}
          recipientType={MessageRecipientType.WAITLIST}
        />
      </>
    ),
  });
  const shouldShowNoResultsFound = !isLoadingData && !data.length;

  // fetching waiting list to show in the tab

  return (
    <TabContext value={activeTab}>
      {!isInEventMode && (
        <Tabs value={activeTab} onChange={handleChange}>
          <Tab
            label={
              <Grid container flexDirection="column">
                {t('ordersTabs.orders')}
              </Grid>
            }
            value={ParticipantsTableTabValue.ORDERS}
          />
          <Tab
            label={
              <Grid container gap={0.5}>
                {data.length > 0 && (
                  <Grid>
                    <StyledWaitingListNotEmpty style={{ backgroundColor: '#ffa726' }} />
                  </Grid>
                )}
                <Grid>{t('ordersTabs.waitingList')}</Grid>
              </Grid>
            }
            value={ParticipantsTableTabValue.WAITING_LIST}
          />
        </Tabs>
      )}
      <TabPanel value={ParticipantsTableTabValue.ORDERS} style={{ padding: 0 }}>
        <Grid
          container
          flexDirection="column"
          style={{ height: isSmallScreen ? undefined : '60vh', overflow: 'hidden' }}>
          <EventParticipantsTable
            setOrder={setOrder}
            event={event}
            orderPage={orderPage}
            setEditOrderIsOpen={setEditOrderIsOpen}
            setCheckInDialogIsOpen={setCheckInDialogIsOpen}
            isInEventMode={isInEventMode}
          />
        </Grid>
      </TabPanel>
      <TabPanel value={ParticipantsTableTabValue.WAITING_LIST} style={{ padding: 0 }}>
        <Grid container flexDirection="column" style={{ height: height < 674 ? '50vh' : '60vh', overflow: 'hidden' }}>
          <Grid
            container
            flexDirection="column"
            className="event-participant-table"
            overflow="hidden"
            mb={4}
            mt={1}
            border="1px solid #555555"
            borderRadius="4px">
            {shouldShowNoResultsFound ? (
              <Grid container m={4}>
                {t('waitingList.nonfound')}
              </Grid>
            ) : (
              WaitingListTableComponent
            )}
          </Grid>
        </Grid>
        <WaitingListUpdateDialog
          open={waitingListUpdateDialogIsOpen}
          setOpen={setWaitingListUpdateDialogIsOpen}
          waitingListItem={selectedWaitingListItem}
          onUpdate={(updatedItem: WaitingListItemData) => {
            const items = data?.map((item) => (item.id === updatedItem.id ? updatedItem : item)) || [];
            void mutate(items);
          }}
        />
        <EditOrderDialog
          editOrderDialogState={editOrderDrawerState}
          closeEditOrderDialog={() => {
            void mutate();
            void closeEditOrderDrawer();
          }}
        />
        <OrderBoxDialog open={orderOpen} setOpen={setOrderOpen} orderId={orderId} />
      </TabPanel>
    </TabContext>
  );
};
